import React from "react";
import { ShoppingBag } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { formatCurrency, calculateCommission, getCommissionPercentage } from "@/lib/commission";
import type { CouponAnalysis } from "@/types/coupon";

interface SalesHistoryProps {
  couponAnalysis: CouponAnalysis | null;
  loading?: boolean;
}

function maskEmail(email: string): string {
  if (!email || !email.includes("@")) return "*****";
  const [localPart, domain] = email.split("@");
  const maskedLocal = localPart.substring(0, Math.min(3, localPart.length)) + "*****";
  return `${maskedLocal}@${domain}`;
}

export const SalesHistory: React.FC<SalesHistoryProps> = ({ couponAnalysis, loading }) => {
  if (loading && !couponAnalysis) {
    return (
      <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 border-white/10 bg-white/[0.03] backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Historico de Vendas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-48 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-20 bg-muted rounded animate-pulse" />
                </div>
                <div className="space-y-2 text-right">
                  <div className="h-4 w-20 bg-muted rounded animate-pulse ml-auto" />
                  <div className="h-3 w-16 bg-muted rounded animate-pulse ml-auto" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500 border-white/10 bg-white/[0.03] backdrop-blur-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Historico de Vendas
          </CardTitle>
          {couponAnalysis && couponAnalysis.orders.all.length > 0 && (
            <div className="flex gap-2">
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-900 dark:bg-green-900/50 dark:text-green-100 font-semibold px-3 py-1"
              >
                {couponAnalysis.orders.completed.length} Concluidas
              </Badge>
              <Badge
                variant="secondary"
                className="bg-yellow-100 text-yellow-900 dark:bg-yellow-900/50 dark:text-yellow-100 font-semibold px-3 py-1"
              >
                {couponAnalysis.orders.other.length} Pendentes
              </Badge>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {couponAnalysis && couponAnalysis.orders.all.length > 0 ? (
          <div className="space-y-2 max-h-[360px] overflow-y-auto scrollbar-hide lg:scrollbar-hide">
            {couponAnalysis.orders.all
              .sort((a, b) => {
                if (a.status === "completed" && b.status !== "completed") return -1;
                if (a.status !== "completed" && b.status === "completed") return 1;
                return new Date(b.date_created).getTime() - new Date(a.date_created).getTime();
              })
              .map((order) => {
                const isCompleted = order.status === "completed";
                const commission = calculateCommission(
                  order.total,
                  order.shipping_total || 0,
                  couponAnalysis.commission_rate
                );
                const tooltipText = `Comissao: (Total: ${formatCurrency(order.total)} - Frete: ${formatCurrency(
                  order.shipping_total || 0
                )}) x ${getCommissionPercentage(couponAnalysis.commission_rate)} = ${formatCurrency(commission)}`;

                return (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 border-border hover:bg-muted/50 transition-all duration-200 depth-1 depth-transition depth-hover-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">Pedido #{order.number}</p>
                        <Badge
                          variant={isCompleted ? "default" : "secondary"}
                          className={
                            isCompleted
                              ? "bg-green-100 text-green-900 dark:bg-green-900/50 dark:text-green-100 font-semibold px-2.5 py-0.5"
                              : "bg-yellow-100 text-yellow-900 dark:bg-yellow-900/50 dark:text-yellow-100 font-semibold px-2.5 py-0.5"
                          }
                        >
                          {isCompleted ? "Concluido" : "Pendente"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{maskEmail(order.customer_email)}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(order.date_created).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}{" "}
                        {new Date(order.date_created).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      <p className="font-semibold">{formatCurrency(order.total)}</p>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`text-sm font-medium cursor-help ${
                              isCompleted
                                ? "text-green-600 dark:text-green-400"
                                : "text-yellow-600 dark:text-yellow-400"
                            }`}
                          >
                            {isCompleted ? "+" : "("}
                            {formatCurrency(commission)}
                            {isCompleted ? "" : ")"}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent sideOffset={8}>{tooltipText}</TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="text-center py-12">
            <ShoppingBag className="h-16 w-16 text-muted-foreground/40 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nenhuma venda encontrada</h3>
            <p className="text-muted-foreground mb-4">Compartilhe seu cupom para comecar a vender.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
